import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Trash2, Loader2, Plus, ArrowRight, LayoutDashboard } from "lucide-react";
import {
  listPortfolios,
  createPortfolio,
  deletePortfolio,
  formatApiError,
  getAggregate,
  getSnapshots,
  toastApiError,
} from "@/lib/api";

const DEFAULT_CASH = 100000;
const MIN_CASH = 1000;
const MAX_CASH = 10000000;

const PIE_COLORS = [
  "#C9A227", "#8B6CC1", "#3D9970", "#4B2E83", "#D08A4E",
  "#A9A9B6", "#4A4A56", "#E2E2EA",
];

const fmtUsd = (n) =>
  Number(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export default function PortfoliosPage() {
  const [items, setItems] = useState(null);
  const [aggregate, setAggregate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [cash, setCash] = useState(DEFAULT_CASH);
  const [err, setErr] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const nav = useNavigate();

  const refresh = async () => {
    try {
      const [list, agg] = await Promise.all([listPortfolios(), getAggregate()]);
      setItems(list);
      setAggregate(agg);
    } catch (e) {
      toastApiError(e);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setCreating(true);
    try {
      await createPortfolio({ name: name.trim(), starting_cash: Number(cash) });
      toast.success(`Created "${name}"`);
      setName("");
      setCash(DEFAULT_CASH);
      setDialogOpen(false);
      refresh();
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toastApiError(e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      {aggregate && items && items.length > 0 ? (
        <div
          data-testid="portfolios-aggregate"
          className="mb-6 grid grid-cols-1 gap-4 border border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-5 md:grid-cols-[minmax(0,1fr)_260px]"
        >
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <Stat
              label="Combined Equity"
              value={fmtUsd(aggregate.combined_equity)}
              testid="agg-equity"
              gold
            />
            <Stat
              label="Total Return"
              value={`${aggregate.combined_return >= 0 ? "+" : ""}${fmtUsd(
                aggregate.combined_return,
              )}`}
              testid="agg-return"
              sub={`${aggregate.combined_return_pct >= 0 ? "+" : ""}${Number(
                aggregate.combined_return_pct,
              ).toFixed(2)}%`}
              positive={aggregate.combined_return > 0}
              negative={aggregate.combined_return < 0}
            />
            <Stat
              label="Realized"
              value={`${aggregate.realized_pnl_total >= 0 ? "+" : ""}${fmtUsd(
                aggregate.realized_pnl_total,
              )}`}
              testid="agg-realized"
              positive={aggregate.realized_pnl_total > 0}
              negative={aggregate.realized_pnl_total < 0}
            />
            <Stat
              label="Unrealized"
              value={`${aggregate.unrealized_pnl_total >= 0 ? "+" : ""}${fmtUsd(
                aggregate.unrealized_pnl_total,
              )}`}
              testid="agg-unrealized"
              positive={aggregate.unrealized_pnl_total > 0}
              negative={aggregate.unrealized_pnl_total < 0}
            />
          </div>
          <AllocationPie allocation={aggregate.allocation} />
        </div>
      ) : null}

      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
            Your portfolios
          </h1>
          <div className="mt-1 text-2xl text-[var(--aurion-text)]">
            {items ? `${items.length} paper portfolio${items.length === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-portfolio-btn"
              className="h-11 border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 font-semibold tracking-widest text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
            >
              <Plus className="mr-2 h-4 w-4" />
              New portfolio
            </Button>
          </DialogTrigger>
          <DialogContent
            data-testid="new-portfolio-dialog"
            className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]"
          >
            <DialogHeader>
              <DialogTitle>Create a new portfolio</DialogTitle>
              <DialogDescription className="text-[var(--aurion-muted)]">
                Choose a name and starting cash. All portfolios are paper — no
                real money.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="p-name">Name</Label>
                <Input
                  id="p-name"
                  data-testid="portfolio-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Momentum sleeve"
                  required
                  className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
                />
              </div>
              <div>
                <Label htmlFor="p-cash">
                  Starting cash (USD, {fmtUsd(MIN_CASH)}–{fmtUsd(MAX_CASH)})
                </Label>
                <Input
                  id="p-cash"
                  data-testid="portfolio-cash-input"
                  type="number"
                  min={MIN_CASH}
                  max={MAX_CASH}
                  step={100}
                  value={cash}
                  onChange={(e) => setCash(e.target.value)}
                  required
                  className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono"
                />
              </div>
              {err ? (
                <div data-testid="portfolio-create-error" className="text-sm text-red-400">
                  {err}
                </div>
              ) : null}
              <DialogFooter>
                <Button
                  data-testid="portfolio-create-submit"
                  type="submit"
                  disabled={creating}
                  className="h-11 border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 font-semibold tracking-widest text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
                >
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items === null ? (
          <div className="text-[var(--aurion-muted)]">Loading…</div>
        ) : items.length === 0 ? (
          <Card
            data-testid="empty-portfolios"
            className="col-span-full border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-10 text-center text-sm text-[var(--aurion-muted)]"
          >
            No portfolios yet. Create your first paper portfolio to get started.
          </Card>
        ) : (
          items.map((p) => (
            <PortfolioCard
              key={p.id}
              p={p}
              onOpen={() => nav(`/portfolios/${p.id}`)}
              onDeleted={refresh}
            />
          ))
        )}
      </div>
    </div>
  );
}

function PortfolioCard({ p, onOpen, onDeleted }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    setBusy(true);
    try {
      await deletePortfolio(p.id);
      toast.success(`Deleted "${p.name}"`);
      setConfirmOpen(false);
      onDeleted();
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      data-testid={`portfolio-card-${p.id}`}
      className="group flex flex-col border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-5 transition-colors hover:border-[var(--aurion-gold)]/40"
    >
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
          Portfolio
        </div>
        <div className="mt-1 text-lg text-[var(--aurion-text)]" data-testid="portfolio-name">
          {p.name}
        </div>
        <div className="mt-4 font-mono text-2xl" style={{ color: "var(--aurion-gold)" }}>
          {fmtUsd(p.total_equity ?? p.cash)}
        </div>
        <div className="mt-1 flex items-baseline gap-2 text-[10px] uppercase tracking-widest">
          <span className="text-[var(--aurion-muted)]">
            cash · {fmtUsd(p.cash)}
          </span>
          {p.total_return !== undefined ? (
            <span
              data-testid={`portfolio-return-${p.id}`}
              className={
                Number(p.total_return) > 0
                  ? "text-emerald-400"
                  : Number(p.total_return) < 0
                  ? "text-red-400"
                  : "text-[var(--aurion-muted)]"
              }
            >
              {Number(p.total_return_pct) >= 0 ? "+" : ""}
              {Number(p.total_return_pct || 0).toFixed(2)}%
            </span>
          ) : null}
        </div>
        {p.positions && p.positions.length > 0 ? (
          <div className="mt-2 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
            {p.positions.length} position{p.positions.length === 1 ? "" : "s"}
          </div>
        ) : null}
        <PortfolioSparkline portfolioId={p.id} />
      </div>
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            data-testid={`portfolio-open-${p.id}`}
            variant="ghost"
            onClick={onOpen}
            className="p-0 text-xs uppercase tracking-widest text-[var(--aurion-gold)] hover:bg-transparent"
          >
            Detail <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
          <Link
            data-testid={`portfolio-desk-${p.id}`}
            to={`/desk/${p.id}`}
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[var(--aurion-lavender)] hover:text-[var(--aurion-gold)]"
          >
            <LayoutDashboard className="h-3.5 w-3.5" />
            Desk
          </Link>
        </div>
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid={`portfolio-delete-${p.id}`}
              variant="ghost"
              size="icon"
              className="text-[var(--aurion-muted)] hover:bg-transparent hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent
            data-testid="delete-portfolio-dialog"
            className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]"
          >
            <DialogHeader>
              <DialogTitle>Delete "{p.name}"?</DialogTitle>
              <DialogDescription className="text-[var(--aurion-muted)]">
                This cannot be undone. Type the portfolio name to confirm.
              </DialogDescription>
            </DialogHeader>
            <Input
              data-testid="delete-confirm-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
              placeholder={p.name}
            />
            <DialogFooter>
              <Button
                data-testid="delete-confirm-submit"
                onClick={doDelete}
                disabled={busy || typed !== p.name}
                className="border border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Delete permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
}

function Stat({ label, value, sub, testid, gold, positive, negative }) {
  const color = gold
    ? "text-[var(--aurion-gold)]"
    : positive
    ? "text-[var(--aurion-green)]"
    : negative
    ? "text-[var(--aurion-red)]"
    : "text-[var(--aurion-text)]";
  return (
    <div data-testid={testid}>
      <div className="aurion-section-title">{label}</div>
      <div className={`mt-1 font-mono text-lg ${color}`}>{value}</div>
      {sub ? (
        <div
          className={`text-[10px] font-mono uppercase tracking-widest ${
            positive
              ? "text-[var(--aurion-green)]"
              : negative
              ? "text-[var(--aurion-red)]"
              : "text-[var(--aurion-muted)]"
          }`}
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function AllocationPie({ allocation }) {
  const rows = (allocation || []).filter((a) => (a.market_value || 0) > 0);
  const total = rows.reduce((s, a) => s + (a.market_value || 0), 0) || 1;
  return (
    <div data-testid="portfolios-allocation" className="flex items-center gap-3">
      <div className="h-32 w-32 flex-shrink-0">
        {rows.length === 0 ? (
          <div className="flex h-full w-full items-center justify-center border border-[var(--aurion-border)] text-[10px] text-[var(--aurion-muted)]">
            no holdings
          </div>
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={rows}
                dataKey="market_value"
                nameKey="symbol"
                innerRadius={30}
                outerRadius={55}
                stroke="var(--aurion-panel)"
              >
                {rows.map((r, i) => (
                  <Cell
                    key={r.symbol}
                    fill={
                      r.symbol === "CASH"
                        ? "var(--aurion-border-strong)"
                        : PIE_COLORS[i % PIE_COLORS.length]
                    }
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        {rows.slice(0, 5).map((r, i) => (
          <div key={r.symbol} className="flex items-center gap-2 text-[11px]">
            <span
              className="inline-block h-2 w-2"
              style={{
                background:
                  r.symbol === "CASH"
                    ? "var(--aurion-border-strong)"
                    : PIE_COLORS[i % PIE_COLORS.length],
              }}
            />
            <span className="font-mono text-[var(--aurion-text)]">{r.symbol}</span>
            <span className="ml-auto font-mono text-[10px] text-[var(--aurion-muted)]">
              {((r.market_value / total) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortfolioSparkline({ portfolioId }) {
  const [pts, setPts] = useState([]);
  useEffect(() => {
    let alive = true;
    getSnapshots(portfolioId, 60)
      .then((r) => {
        if (!alive) return;
        setPts((r.items || []).map((s) => Number(s.equity || 0)));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [portfolioId]);
  if (pts.length < 2) return null;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const path = pts
    .map((v, i) => {
      const x = (i / (pts.length - 1)) * 100;
      const y = 20 - ((v - min) / span) * 20;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const up = pts[pts.length - 1] >= pts[0];
  return (
    <svg
      data-testid={`portfolio-spark-${portfolioId}`}
      viewBox="0 0 100 20"
      preserveAspectRatio="none"
      className="mt-3 h-8 w-full opacity-70"
    >
      <path
        d={path}
        fill="none"
        stroke={up ? "var(--aurion-gold)" : "var(--aurion-red)"}
        strokeWidth={1.2}
      />
    </svg>
  );
}

